import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Send, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '../components/ui/Button';
import { Field, Input, Select } from '../components/ui/Field';
import { createInvoice, listClients, listInvoices, sendInvoice, type CreateInvoiceInput } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatMoney } from '../lib/utils';

interface DraftLine {
  id: string;
  description: string;
  quantity: string;
  unitAmount: string;
}

export function InvoicesPage() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [clientId, setClientId] = useState('');
  const [currency, setCurrency] = useState('ZAR');
  const [dueDate, setDueDate] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([{ id: crypto.randomUUID(), description: '', quantity: '1', unitAmount: '' }]);

  const clientsQuery = useQuery({ queryKey: ['clients'], queryFn: () => listClients(token!) });
  const invoicesQuery = useQuery({ queryKey: ['invoices'], queryFn: () => listInvoices(token!) });
  const clients = clientsQuery.data?.clients ?? [];
  const invoices = invoicesQuery.data?.invoices ?? [];
  const selectedClient = clients.find((client) => client.clientId === clientId);
  const draftTotalCents = useMemo(
    () =>
      lines.reduce((sum, line) => {
        const quantity = Number(line.quantity);
        const amount = Number(line.unitAmount);
        return sum + (Number.isFinite(quantity) && Number.isFinite(amount) ? Math.round(quantity * amount * 100) : 0);
      }, 0),
    [lines]
  );

  const createMutation = useMutation({
    mutationFn: () => createInvoice(token!, buildInvoiceInput(clientId, currency, dueDate, lines)),
    onSuccess: async () => {
      setLines([{ id: crypto.randomUUID(), description: '', quantity: '1', unitAmount: '' }]);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['invoices'] }),
        queryClient.invalidateQueries({ queryKey: ['client', clientId] })
      ]);
    }
  });
  const sendMutation = useMutation({
    mutationFn: (invoiceId: string) => sendInvoice(token!, invoiceId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invoices'] });
    }
  });

  return (
    <section className="grid gap-6 lg:grid-cols-[420px_1fr]">
      <form
        className="rounded-sm bg-white p-5 shadow-sm ring-1 ring-ink/10"
        onSubmit={(event) => {
          event.preventDefault();
          createMutation.mutate();
        }}
      >
        <h1 className="text-xl font-semibold tracking-normal">Create invoice</h1>
        <div className="mt-5 grid gap-4">
          <Field label="Client">
            <Select value={clientId} onChange={(event) => setClientId(event.target.value)} required>
              <option value="">Select client</option>
              {clients.map((client) => (
                <option key={client.clientId} value={client.clientId}>
                  {client.displayName} ({client.email})
                </option>
              ))}
            </Select>
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Currency">
              <Input value={currency} onChange={(event) => setCurrency(event.target.value.toUpperCase())} maxLength={3} required />
            </Field>
            <Field label="Due date">
              <Input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
            </Field>
          </div>

          <div className="grid gap-3">
            {lines.map((line, index) => (
              <div key={line.id} className="grid gap-3 rounded-sm border border-ink/10 p-3">
                <Field label="Description">
                  <Input
                    value={line.description}
                    onChange={(event) => updateLine(lines, setLines, line.id, { description: event.target.value })}
                    placeholder="Monthly platform access"
                    required
                  />
                </Field>
                <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                  <Field label="Qty">
                    <Input value={line.quantity} onChange={(event) => updateLine(lines, setLines, line.id, { quantity: event.target.value })} inputMode="decimal" required />
                  </Field>
                  <Field label="Amount">
                    <Input value={line.unitAmount} onChange={(event) => updateLine(lines, setLines, line.id, { unitAmount: event.target.value })} inputMode="decimal" required />
                  </Field>
                  <Button
                    className="mt-7"
                    variant="ghost"
                    size="icon"
                    type="button"
                    aria-label={`Remove line ${index + 1}`}
                    onClick={() => setLines((current) => current.filter((item) => item.id !== line.id))}
                    disabled={lines.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setLines((current) => [...current, { id: crypto.randomUUID(), description: '', quantity: '1', unitAmount: '' }])}
            >
              <Plus className="h-4 w-4" />
              <span>Add line</span>
            </Button>
            <p className="text-sm font-semibold">{formatMoney(draftTotalCents, currency || 'ZAR')}</p>
          </div>

          <Button type="submit" disabled={!selectedClient || createMutation.isPending}>
            Generate PDF
          </Button>
          {createMutation.error ? <p className="text-sm text-clay">{createMutation.error.message}</p> : null}
        </div>
      </form>

      <section className="rounded-sm bg-white shadow-sm ring-1 ring-ink/10">
        <div className="border-b border-ink/10 px-4 py-3">
          <h2 className="font-semibold">Invoices</h2>
        </div>
        <div className="divide-y divide-ink/10">
          {invoices.map((invoice) => (
            <div key={invoice.invoiceId} className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium">{invoice.invoiceNumber}</p>
                  <span className="rounded-full bg-ink/10 px-2 py-0.5 text-xs font-semibold text-ink/70">{invoice.status}</span>
                </div>
                <p className="mt-1 text-sm text-ink/70">{invoice.clientName} - {formatMoney(invoice.totalCents, invoice.currency)}</p>
                <p className="mt-1 text-xs text-ink/70">Created {formatDate(invoice.createdAt)}</p>
              </div>
              <Button
                variant="secondary"
                disabled={invoice.status === 'sent' || sendMutation.isPending}
                onClick={() => sendMutation.mutate(invoice.invoiceId)}
              >
                <Send className="h-4 w-4" />
                <span>{invoice.status === 'sent' ? 'Sent' : 'Send'}</span>
              </Button>
            </div>
          ))}
          {invoices.length === 0 ? <p className="px-4 py-10 text-sm text-ink/70">{invoicesQuery.isLoading ? 'Loading invoices' : 'No invoices yet'}</p> : null}
        </div>
      </section>
    </section>
  );
}

function updateLine(lines: DraftLine[], setLines: (lines: DraftLine[]) => void, id: string, patch: Partial<DraftLine>) {
  setLines(lines.map((line) => (line.id === id ? { ...line, ...patch } : line)));
}

function buildInvoiceInput(clientId: string, currency: string, dueDate: string, lines: DraftLine[]): CreateInvoiceInput {
  return {
    clientId,
    currency,
    dueDate: dueDate || undefined,
    lineItems: lines.map((line) => ({
      description: line.description,
      quantity: Number(line.quantity),
      unitAmountCents: Math.round(Number(line.unitAmount) * 100)
    }))
  };
}
